import "server-only";
import { query } from "../connection";

export type PlanRow = {
  id: number;
  code: string;
  name: string;
  monthly_price: string; // decimal renvoyé en string par mysql2
  yearly_price: string;
  max_technicians: number;
  max_sites: number;
  max_machines: number;
  max_templates: number;
  features: string | null; // JSON serialisé côté MySQL — on le parse côté React
  visible: number;
  sort_order: number;
  // Compteur dérivé : nombre de subscriptions actives sur ce plan
  active_subscriptions: number;
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------
//
// Le panel a un GRANT UPDATE ciblé sur la table `plans` (cf. AGENTS.md
// "Périmètre des mutations admin"). Toute autre table reste read-only pour
// le panel — pour suspendre/cancel un tenant, etc., on passe par
// missioflow-app.
//
// Tous les champs sont passés via `?` placeholders, jamais de concat avec
// l'input utilisateur. Validation des types côté Route Handler avant
// l'appel ici.

export type UpdatablePlanFields = Partial<{
  name: string;
  monthly_price: number;
  yearly_price: number;
  max_technicians: number;
  max_sites: number;
  max_machines: number;
  max_templates: number;
  features: string[]; // sera serialisé en JSON
  visible: boolean;
}>;

export async function getPlan(id: number): Promise<PlanRow | null> {
  const rows = await query<PlanRow>(
    `SELECT
       p.id, p.code, p.name, p.monthly_price, p.yearly_price,
       p.max_technicians, p.max_sites, p.max_machines, p.max_templates,
       p.features, p.visible, p.sort_order,
       (SELECT COUNT(*) FROM tenant_subscriptions ts
         WHERE ts.plan_id = p.id AND ts.status IN ('trialing','active','past_due')
       ) AS active_subscriptions
     FROM plans p WHERE p.id = ? LIMIT 1`,
    [id],
  );
  return rows[0] ?? null;
}

export async function updatePlan(
  id: number,
  fields: UpdatablePlanFields,
): Promise<void> {
  // On construit dynamiquement la liste des SET — clés whitelisted ci-dessous
  // pour éviter qu'un caller passe `password_hash` ou autre.
  const ALLOWED = [
    "name",
    "monthly_price",
    "yearly_price",
    "max_technicians",
    "max_sites",
    "max_machines",
    "max_templates",
    "features",
    "visible",
  ] as const;

  const setParts: string[] = [];
  const params: unknown[] = [];
  for (const key of ALLOWED) {
    if (!(key in fields)) continue;
    const value = fields[key];
    if (value === undefined) continue;
    setParts.push(`${key} = ?`);
    if (key === "features") {
      params.push(JSON.stringify(value ?? []));
    } else if (key === "visible") {
      params.push(value ? 1 : 0);
    } else {
      params.push(value);
    }
  }

  if (setParts.length === 0) {
    throw new Error("Aucun champ à mettre à jour.");
  }
  params.push(id);

  await query(`UPDATE plans SET ${setParts.join(", ")} WHERE id = ?`, params);
}

// ---------------------------------------------------------------------------

export async function listPlans(): Promise<PlanRow[]> {
  return query<PlanRow>(`
    SELECT
      p.id, p.code, p.name, p.monthly_price, p.yearly_price,
      p.max_technicians, p.max_sites, p.max_machines, p.max_templates,
      p.features, p.visible, p.sort_order,
      (SELECT COUNT(*) FROM tenant_subscriptions ts
        WHERE ts.plan_id = p.id AND ts.status IN ('trialing','active','past_due')
      ) AS active_subscriptions
    FROM plans p
    ORDER BY p.sort_order ASC, p.id ASC
  `);
}
